import React, { useState, useEffect, useCallback, useMemo, memo, useRef } from 'react';
import { ListItem, ListItemText, Slider, Box } from '@mui/material';
import { withStyles } from 'tss-react/mui';
import { previewHistoryBatch } from '../../pages/CreatePage/store/extensions';
import SliderTitle from './SliderTitle';
import { calculateValue } from '../../../utilities/mathUtilities';
import { SliderInput } from './SliderInput';

const styles = () => ({
    input: {
        width: 38,
        fontSize: 14,
        fontWeight: 500
    },
    listItemText: {
        paddingRight: 14,
        marginBottom: 0,
    },
    listItemTextRange: {
        paddingRight: 80
    },
    hideArrows: {
        '&::-webkit-inner-spin-button': {
            WebkitAppearance: 'none' 
        },
        '&::-webkit-outer-spin-button': {
            WebkitAppearance: 'none'
        },
    }
});

const SliderListItem = memo(({
    title,
    hideArrows,
    displayMultiplier = 1,
    onValueChange,
    toolTip,
    value = 0,
    step = 1,
    min = 0,
    max = 100,
    scale,
    classes,
    preventRound = false,
    disabled = false
}) => {
    // Memoize initial value calculation
    const initialValue = useMemo(() => 
        calculateValue(value, displayMultiplier, preventRound, step, min, max),
        [value, displayMultiplier, preventRound, step, min, max]
    );

    const [displayValue, setDisplayValue] = useState(initialValue);

    // Only an edit made on this control is worth reporting. A value that arrived through props is
    // already the parent's own, and sending it back turns one change into several: the parent
    // re-renders, this notifies it again, and the settings bounce through stale values on the way.
    const changedByUser = useRef(false);

    // Memoize handlers
    const handleSliderChange = useCallback((_, value) => {
        changedByUser.current = true;
        setDisplayValue(value);
    }, []);

    // The number input's max attribute only limits its arrows, not what the user types. An unclamped
    // value reaches the store as is while the slider shows max, and the export rejects it (bug 5076).
    const handleInputChange = useCallback((event) => {
        const typedValue = Number(event.target.value);
        const newValue = Math.min(Math.max(typedValue, min), max);

        changedByUser.current = true;
        setDisplayValue(newValue);
    }, [min, max]);

    const onFocus = useCallback(() => {
        previewHistoryBatch.start();
    }, []);

    const onBlur = useCallback(() => {
        previewHistoryBatch.end();
    }, []);

    const onSliderChangeCommitted = useCallback(() => {
        previewHistoryBatch.end();
    }, []);

    // Update parent after an edit made here. The flag also covers initialization, where the value
    // came from the parent and there is nothing to report back.
    useEffect(() => {
        if (!changedByUser.current)
            return;

        changedByUser.current = false;
        onValueChange(displayValue / displayMultiplier);
    }, [displayValue, displayMultiplier, onValueChange]);

    // Update local state when props change
    useEffect(() => {
        const newValue = calculateValue(value, displayMultiplier, preventRound, step, min, max);
        if (newValue !== displayValue) {
            setDisplayValue(newValue);
        }
    }, [value, displayMultiplier, preventRound, step, min, max]);

    return (
        <ListItem className={classes.paddingBottom}>
            <ListItemText className={classes.listItemText}>
                <SliderTitle title={title} toolTip={toolTip} />
                <Slider
                    size='small'
                    value={displayValue}
                    onChange={handleSliderChange}
                    step={step}
                    min={min}
                    max={max}
                    valueLabelDisplay='auto'
                    scale={scale}
                    disabled={disabled}
                    onChangeCommitted={onSliderChangeCommitted}
                    onBlur={onBlur}
                    onFocus={onFocus}
                    sx={{
                        ".MuiSlider-valueLabel": {
                            backgroundColor: "primary.main",
                        }
                    }}
                />
            </ListItemText>
            <Box>
                <SliderInput
                    value={displayValue}
                    step={step}
                    min={min}
                    max={max}
                    hideArrows={hideArrows}
                    classes={classes}
                    disabled={disabled}
                    onChange={handleInputChange}
                    onFocus={onFocus}
                    onBlur={onBlur}
                />
            </Box>
        </ListItem>
    );
});

export default withStyles(SliderListItem, styles);